/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 */

import React, { useState } from 'react';
import { Icon, Popup } from 'semantic-ui-react';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// expected props
// - id
// - pending, as boolean
// - terminationLocked, as boolean
// - can, as { start, stop, terminate, lock }
// - onAction(action, id)
export default function ActionButtons({ id, pending = false, terminationLocked, can, onAction }) {
  const [confirmTerminate, setConfirmTerminate] = useState(false);

  function handleAction(action, value) {
    return (e) => {
      e.stopPropagation();
      onAction(action, value);
    };
  }

  return (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap' }}>
      <Popup
        trigger={
          <button
            className="workspace-action-btn-icon workspace-action-btn-secondary"
            onClick={handleAction('view', `/workspaces/id/${id}`)}
            style={{ padding: '6px 8px' }}
          >
            <Icon name="eye" style={iconStyle} />
          </button>
        }
        content="View Details"
        position="top center"
        mouseEnterDelay={500}
        inverted
        size="mini"
      />
      {can.start && (
        <Popup
          trigger={
            <button
              className="workspace-action-btn-icon workspace-action-btn-start"
              onClick={handleAction('start', id)}
              disabled={pending}
              style={{ padding: '6px 8px' }}
            >
              <Icon name={pending ? 'spinner' : 'play'} loading={pending} style={iconStyle} />
            </button>
          }
          content="Start"
          position="top center"
          mouseEnterDelay={500}
          inverted
          size="mini"
        />
      )}
      {can.stop && (
        <Popup
          trigger={
            <button
              className="workspace-action-btn-icon workspace-action-btn-stop"
              onClick={handleAction('stop', id)}
              disabled={pending}
              style={{ padding: '6px 8px' }}
            >
              <Icon name={pending ? 'spinner' : 'stop'} loading={pending} style={iconStyle} />
            </button>
          }
          content="Stop"
          position="top center"
          mouseEnterDelay={500}
          inverted
          size="mini"
        />
      )}
      {can.terminate && (
        terminationLocked ? (
          <Popup
            trigger={
              <button
                className="workspace-action-btn-icon"
                disabled
                style={{ padding: '6px 8px', opacity: 0.4 }}
              >
                <Icon name="trash" style={iconStyle} />
              </button>
            }
            content="Locked - cannot terminate"
            position="top center"
            mouseEnterDelay={500}
            inverted
            size="mini"
          />
        ) : confirmTerminate ? (
          <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
            <button
              className="workspace-action-btn-icon workspace-action-btn-terminate"
              onClick={(e) => { e.stopPropagation(); onAction('terminate', id); setConfirmTerminate(false); }}
              style={{ padding: '6px 8px' }}
            >
              <Icon name="check" style={iconStyle} />
            </button>
            <button
              className="workspace-action-btn-icon workspace-action-btn-secondary"
              onClick={(e) => { e.stopPropagation(); setConfirmTerminate(false); }}
              style={{ padding: '6px 8px' }}
            >
              <Icon name="close" style={iconStyle} />
            </button>
          </div>
        ) : (
          <Popup
            trigger={
              <button
                className="workspace-action-btn-icon workspace-action-btn-terminate"
                onClick={(e) => { e.stopPropagation(); setConfirmTerminate(true); }}
                disabled={pending}
                style={{ padding: '6px 8px' }}
              >
                <Icon name={pending ? 'spinner' : 'trash'} loading={pending} style={iconStyle} />
              </button>
            }
            content="Terminate"
            position="top center"
            mouseEnterDelay={500}
            inverted
            size="mini"
          />
        )
      )}
      {can.lock && (
        <Popup
          trigger={
            <button
              className="workspace-action-btn-icon workspace-action-btn-secondary"
              onClick={handleAction('toggleLock', id)}
              disabled={pending}
              style={{ padding: '6px 8px' }}
            >
              <Icon name={terminationLocked ? 'unlock' : 'lock'} style={iconStyle} />
            </button>
          }
          content={terminationLocked ? 'Unlock Termination' : 'Lock Termination'}
          position="top center"
          mouseEnterDelay={500}
          inverted
          size="mini"
        />
      )}
    </div>
  );
}
